import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import SaveButton from "../../components/admin/SaveButton";
import { useUploadStatus } from "../../context/UploadStatusContext";

const empty = {
    name: "", slug: "", category: "Wedding", short_description: "", long_description: "",
    image_url: "", gallery: [], price: "", order: 0, is_active: true,
};

const CATEGORIES = ["Wedding", "Wedding Cake Hire", "Birthday", "Baby Shower", "Engagement", "Anniversary", "Christening", "Bento", "Cupcakes", "Cake-in-a-Jar"];

export default function AdminCakes() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const { isUploading } = useUploadStatus();

    const load = () => api.get("/cakes?active_only=false").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing("new"); setForm(empty); };
    const openEdit = (c) => { setEditing(c.id); setForm({ ...c, price: c.price ?? "" }); };
    const close = () => { setEditing(null); setForm(empty); };

    const save = async (e) => {
        e.preventDefault();
        if (isUploading) return;
        const payload = { ...form, price: form.price === "" ? null : parseFloat(form.price) };
        try {
            if (editing === "new") await api.post("/cakes", payload);
            else await api.put(`/cakes/${editing}`, payload);
            toast.success("Saved"); close(); load();
        } catch { toast.error("Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this cake?")) return;
        try { await api.delete(`/cakes/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    const toggleActive = async (c) => {
        try {
            await api.put(`/cakes/${c.id}`, { ...c, is_active: !c.is_active });
            toast.success(c.is_active ? "Disabled — hidden from site" : "Enabled — now visible");
            load();
        } catch { toast.error("Update failed"); }
    };

    return (
        <div data-testid="admin-cakes">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-3xl">Cakes</h2>
                <button onClick={openNew} className="btn-pink" data-testid="add-cake"><Plus size={14} /> New</button>
            </div>

            <div className="space-y-2">
                {items.map((c, i) => (
                    <div key={c.id} className="bg-white border border-[#EAE3DE] p-4 flex items-center gap-4" data-testid={`cake-row-${i}`}>
                        <img src={c.image_url} alt={c.name} className={`w-16 h-16 object-cover ${c.is_active ? "" : "opacity-50"}`} />
                        <div className="flex-1 min-w-0">
                            <p className={`font-display text-lg ${c.is_active ? "" : "opacity-50"}`}>{c.name}</p>
                            <p className="text-xs text-ink-soft">{c.category} · /{c.slug}</p>
                        </div>
                        <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${c.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {c.is_active ? "Active" : "Hidden"}
                        </span>
                        <div className="flex gap-3 items-center ml-2">
                            <button onClick={() => toggleActive(c)} className={c.is_active ? "text-green-600 hover:text-blush" : "text-gray-400 hover:text-blush"} title={c.is_active ? "Disable (hide from site)" : "Enable (show on site)"} data-testid={`toggle-cake-${i}`}>
                                {c.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                            <button onClick={() => openEdit(c)} data-testid={`edit-cake-${i}`}><Edit size={16} /></button>
                            <button onClick={() => remove(c.id)} data-testid={`delete-cake-${i}`}><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-2xl">{editing === "new" ? "New" : "Edit"} Cake</h3>
                            <button onClick={close}><X size={20} /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                                <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Slug (e.g. wedding-cake)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
                            </div>
                            <select className="w-full px-4 py-3 border border-[#EAE3DE] bg-white" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                            </select>
                            <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Short description" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} required />
                            <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={4} placeholder="Long description" value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} required />
                            <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} label="Cake Image" testIdPrefix="cake-image" />
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Price (AUD) — only shown for Wedding Cake Hire</label>
                                <input type="number" step="0.01" min="0" className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="e.g. 600" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <input type="number" className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                    <span className="text-sm">Active</span>
                                </label>
                            </div>
                            <SaveButton />
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
